import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRouter } from "next/navigation";
import { Suspense, useEffect, useRef } from "react";

import { OBJECT_TYPE } from "~/core/constants/common";
import { cn } from "~/core/generic/shadcn-utils";
import { CommandItem } from "~/core/ui/Command";
import If from "~/core/ui/If";

import AvatarWithFallback from "~/components/AvatarWithFallback";
import OctoTag from "~/components/OctoTag";

import {
  faBuilding,
  faBullseyeArrow,
  faUser,
} from "@fortawesome/pro-regular-svg-icons";

import { useGlobalCommandbarStore } from "../../store";
import {
  CommandbarFilterTabType,
  CommandbarItemType,
  type ICommandbarItem,
} from "../../types/common";
import ItemIcon from "../ItemIcon";
import ItemIconLoading from "../ItemIcon/loading";

type CommandbarItemProps = {
  command: ICommandbarItem;
  onClick?: () => void;
  children?: React.ReactNode;
};

export default function CommandbarItem({
  command,
  onClick,
  children,
}: CommandbarItemProps) {
  const router = useRouter();
  const activeItemRef = useRef<HTMLDivElement>(null);

  const setIsOpenGlobalCommandbar = useGlobalCommandbarStore(
    st => st.setIsOpenGlobalCommandbar,
  );
  const activeCommandbarItemId = useGlobalCommandbarStore(
    st => st.activeCommandbarItemId,
  );

  const isAccountOrContactOrOpportunity =
    command.type === CommandbarItemType.ACCOUNT ||
    command.type === CommandbarItemType.CONTACT ||
    command.type === CommandbarItemType.OPPORTUNITY;

  useEffect(() => {
    if (!activeItemRef.current) return;
    activeItemRef.current.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [activeCommandbarItemId]);

  const handleCommandbarItemClick = (command: ICommandbarItem) => {
    if (command.href) {
      router.push(command.href);
    }

    command.onClick?.();
    onClick?.();
    setIsOpenGlobalCommandbar(false);
  };

  return (
    <CommandItem
      className={cn(
        "flex items-center justify-between gap-2 py-2 px-3 w-full",
        "text-light cursor-pointer select-none rounded-base",
        "transition-colors ease-in duration-200",
        "hover:bg-sidebar hover:text-darker",
        "aria-selected:bg-sidebar aria-selected:text-darker",
      )}
      value={command.id}
      ref={activeCommandbarItemId === command.id ? activeItemRef : null}
      onSelect={() => handleCommandbarItemClick(command)}
    >
      <If condition={!children} fallback={children}>
        <div className="flex items-center gap-2">
          <Suspense fallback={<ItemIconLoading />}>
            <If
              condition={isAccountOrContactOrOpportunity}
              fallback={
                <ItemIcon
                  faIcon={command.faIcon}
                  customIcon={command.customIcon}
                />
              }
            >
              <AvatarWithFallback
                src={command.imageUrl}
                alt={command.label || command.description}
                fallbackAlt={command.description || command.label}
                objectType={
                  command.objectType === CommandbarFilterTabType.ACCOUNT
                    ? OBJECT_TYPE.ACCOUNT
                    : command.objectType === CommandbarFilterTabType.CONTACT
                      ? OBJECT_TYPE.CONTACT
                      : OBJECT_TYPE.OPPORTUNITY
                }
                size={20}
                shape={
                  command.objectType === CommandbarFilterTabType.ACCOUNT
                    ? "square"
                    : "circle"
                }
              />
            </If>
          </Suspense>
          <div className="flex items-center gap-2">
            <p>{command.label}</p>
            <p className="text-xs text-light">{command.description}</p>
          </div>
        </div>
        <If
          condition={isAccountOrContactOrOpportunity}
          fallback={
            <If condition={command.type !== CommandbarItemType.PAGE}>
              <p>{command.type}</p>
            </If>
          }
        >
          <OctoTag className="bg-information-lightest text-information-dark">
            <div className="flex items-center gap-1">
              <FontAwesomeIcon
                icon={
                  command.objectType === CommandbarFilterTabType.ACCOUNT
                    ? faBuilding
                    : command.objectType === CommandbarFilterTabType.CONTACT
                      ? faUser
                      : faBullseyeArrow
                }
              />
              {command.type}
            </div>
          </OctoTag>
        </If>
      </If>
    </CommandItem>
  );
}
